/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {info, warn} from '@ion/core/src/logger/logger';
import {loadJobSearchEngine} from '@ion/core/src/services/Connect';
import {DownloadJobsSort} from '@ion/core/src/types/services';
import {JobStatusUpdate} from '@ion/services/src/websocket/types';
import {JobCacheItem} from './JobCacheMerge';
import {createFromStatus} from './JobNotifications';
import {buildTerminalStatusQuery, buildWatchedJobsQuery} from './JobSearchEngineUtils';
import {getJobIds, getNotifiedJobIds} from './WatchedJobs';

const CATCH_UP_PAGE_SIZE = 100;

const CATCH_UP_SORT = [
  {propertyName: 'jobId', order: 'DESC'}
] as unknown as DownloadJobsSort[];

let running = false;

/**
 * Adapts one search row into the shape the shared creation path takes.
 *
 * **REST rows name the timestamp `updatedOn`, not `lastModifiedOn`** - the
 * socket's name for the same server write. Resolving one from the other here is
 * what stops every catch-up notification getting an undefined `occurredAt`.
 *
 * The row also carries the status as the `progressStatus` mirror rather than
 * inside `progress`, and `reason` may be absent entirely, which is why the
 * stored entry's `reason` is optional.
 */
const toStatusUpdate = (row: JobCacheItem): JobStatusUpdate => ({
  name           : row?.name,
  lastModifiedOn : row?.lastModifiedOn ?? row?.updatedOn,
  progress       : {
    status : row?.progress?.status ?? row?.progressStatus ?? '',
    reason : row?.progress?.reason
  }
});

/**
 * Asks whether any watched job finished while nobody was listening, and creates
 * a notification for each one which did.
 *
 * The socket sees events only while connected and there is no replay, so a
 * watched job can reach terminal with nobody listening - while signed out, or
 * during a mid-session socket drop. This runs at sign-in and on every
 * reconnect, and the usual response is zero rows.
 *
 * Two narrowings make that affordable:
 *
 * - **the ids are `jobIds − notifiedJobIds`.** Anything already notified must
 *   never be notified again, so asking about it is wasted at best. Because the
 *   tombstones are persisted, this works from the very first query at sign-in -
 *   the moment the list is longest
 * - **a finished-statuses clause**, which composes onto the ids for free since
 *   they travel in the search text
 *
 *  **No pruning rides this query.** The status filter makes *missing* and
 * *still running* indistinguishable, so a prune here would silently unwatch
 * live jobs - the one unrecoverable failure this feature has.
 *
 * It never throws: a failed catch-up must not fail sign-in, and the next
 * reconnect retries it for free.
 *
 * **It does not read the feature flag**, unlike `JobStatusSubscriptions`'
 * `syncObserved`, which self-gates. The watch list is persisted, so ids survive
 * a flag-off session and an ungated run would query and notify with the feature
 * off - every caller must gate this itself. See `initialization.ts` for why the
 * reconnect call site is safe without one.
 *
 * @param accountId The account to search. The watch list is not account-scoped
 *        but this endpoint is, so a watched job outside this account is not
 *        caught up.
 */
export const runCatchUp = async(accountId: number): Promise<void> => {
  const notified = getNotifiedJobIds();
  const pending  = getJobIds()?.filter(jobId => !notified?.includes(jobId));

  if (!running && accountId && pending?.length) {
    running = true;
    try {
      const {items} = await loadJobSearchEngine({
        accountId,
        query    : `${buildWatchedJobsQuery(pending)} AND ${buildTerminalStatusQuery()}`,
        sortOn   : CATCH_UP_SORT,
        pageSize : CATCH_UP_PAGE_SIZE
      });
      const rows = (items ?? []) as unknown as JobCacheItem[];

      if (rows.length) {
        info(`[ADM] ${rows.length} watched job(s) finished while we were not listening`);
      }
      await rows.reduce<Promise<void>>((queued, row) => queued.then(async() => {
        await createFromStatus(Number(row?.jobId), toStatusUpdate(row));
      }), Promise.resolve());
    } catch (err) {
      warn(
        '[ADM] the watched jobs catch-up query failed: ' +
        `status=${err?.errorCode} ${JSON.stringify(err?.errorText)}`
      );
    } finally {
      running = false;
    }
  }
};

/**
 * Clears the module. Module state outlives a test case, so this exists for test
 * isolation - never call it from application code.
 */
export const reset = (): void => {
  running = false;
};
